import { highlightSegments, type UnsupportedFigure } from "./site-audit-grounding.js";
import { readStoredLabTiming } from "./site-audit-speed.js";
import type { AuditSources, CruxStatus, SourceStatus } from "./site-audit-sources.js";

export interface FactSource {
  id: string;
  label: string;
  status: string;
  detail: string | null;
}

export interface FactRow {
  label: string;
  value: string;
}

export interface SpeedRow {
  url: string;
  strategy: string;
  score: string;
  lcp: string;
  tti: string;
  tbt: string;
  cls: string;
  fieldLcp: string;
  fieldInp: string;
  fieldCls: string;
  fieldNote: string;
}

export interface IssueRow {
  category: string;
  count: string;
  severity: string;
  sampleUrls: string;
}

export interface AuditFacts {
  sourcesRecorded: boolean;
  sources: FactSource[];
  metrics: FactRow[];
  keywords: { keyword: string; position: string; volume: string; traffic: string }[];
  keywordTotal: number;
  competitors: { domain: string; authority: string; traffic: string; keywords: string }[];
  gaps: { keyword: string; volume: string; competitor: string; position: string }[];
  gapTotal: number;
  backlinks: FactRow[];
  onPageStatus: string | null;
  onPageDetail: string | null;
  onPageScore: string;
  pagesCrawled: string;
  issues: IssueRow[];
  speed: SpeedRow[];
  legacyFid: boolean;
  siteChecks: { check: string; subject: string; result: string; detail: string }[];
}

const SOURCE_LABELS: { id: keyof AuditSources; label: string }[] = [
  { id: "domainMetrics", label: "Domain metrics" },
  { id: "rankedKeywords", label: "Ranked keywords" },
  { id: "competitors", label: "Competitors" },
  { id: "keywordGap", label: "Keyword gap" },
  { id: "backlinks", label: "Backlinks" },
  { id: "onPage", label: "On-page crawl" },
  { id: "pagespeed", label: "PageSpeed" },
  { id: "moz", label: "Moz" },
  { id: "crux", label: "Field data" },
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function show(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "number" && Number.isFinite(value)) {
    return Number.isInteger(value) ? value.toLocaleString("en-GB") : String(value);
  }
  return String(value);
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function statusOf(value: unknown): string | null {
  if (!isRecord(value) || typeof value.status !== "string") return null;
  return value.status;
}

/** Staff technical card. A failed crawl has a reason and no score. */
export function onPageSummaryCopy(facts: {
  onPageStatus: string | null;
  onPageScore: string;
  pagesCrawled: string;
  onPageDetail: string | null;
}): string {
  if (facts.onPageStatus === "failed") {
    return facts.onPageDetail ?? "The crawler could not reach the site.";
  }
  const detail = facts.onPageDetail ? ` ${facts.onPageDetail}` : "";
  return `On-page score ${facts.onPageScore}. Pages crawled ${facts.pagesCrawled}.${detail}`;
}

/** A finished crawl collected issues even when the list is empty. Cached is that same crawl, reused. */
export function emptyOnPageIssuesCopy(status: string | null): string {
  return status === "ok" || status === "cached"
    ? "No on-page issues in this run."
    : "On-page issues were not collected.";
}

export function sourceStatusLabel(status: SourceStatus | CruxStatus | string): string {
  switch (status) {
    case "ok":
      return "Collected";
    case "failed":
      return "Failed";
    case "not_collected":
      return "Not collected";
    case "cached":
      return "Cached";
    case "no_field_data":
      return "No field data";
    default:
      return status;
  }
}

function fieldNote(field: unknown, legacy: boolean): { lcp: string; inp: string; cls: string; note: string } {
  if (!isRecord(field)) {
    return {
      lcp: "—",
      inp: "—",
      cls: "—",
      note: legacy ? "Field data was not stored for this run" : "No field data",
    };
  }
  // CrUX blocks are "ok" or "no_field_data". "cached" is a source status, not a field status.
  if (field.status === "ok") {
    const scope = field.scope === "origin" ? "Origin, not this URL" : "This URL";
    return {
      lcp: show(field.lcpMs),
      inp: show(field.inpMs),
      cls: show(field.cls),
      note: scope,
    };
  }
  if (field.status === "no_field_data") {
    return { lcp: "—", inp: "—", cls: "—", note: "No field data" };
  }
  return { lcp: "—", inp: "—", cls: "—", note: "Field data was not stored for this run" };
}

function speedRows(pages: unknown): { rows: SpeedRow[]; legacyFid: boolean } {
  if (!Array.isArray(pages)) return { rows: [], legacyFid: false };
  const rows: SpeedRow[] = [];
  let legacyFid = false;
  for (const page of pages) {
    if (!isRecord(page)) continue;
    const url = typeof page.url === "string" ? page.url : "";
    for (const strategy of ["mobile", "desktop"] as const) {
      const metrics = page[strategy];
      if (!isRecord(metrics)) continue;
      const timing = readStoredLabTiming(metrics);
      if (timing.legacyFid) legacyFid = true;
      const field = fieldNote(metrics.field, timing.legacyFid || metrics.field == null);
      rows.push({
        url,
        strategy,
        score: show(metrics.performanceScore),
        lcp: show(metrics.lcp),
        tti: show(timing.tti),
        tbt: show(metrics.tbt),
        cls: show(metrics.cls),
        fieldLcp: field.lcp,
        fieldInp: field.inp,
        fieldCls: field.cls,
        fieldNote: field.note,
      });
    }
  }
  return { rows, legacyFid };
}

export function readAuditFacts(data: unknown): AuditFacts | null {
  if (!isRecord(data)) return null;
  const sources = isRecord(data.sources) ? data.sources : null;
  const metrics = isRecord(data.domainMetrics) ? data.domainMetrics : {};
  const keywords = Array.isArray(data.topKeywords) ? data.topKeywords : [];
  const competitors = Array.isArray(data.competitorAnalysis) ? data.competitorAnalysis : [];
  const gaps = Array.isArray(data.keywordGaps) ? data.keywordGaps : [];
  const backlinks = isRecord(data.backlinks) ? data.backlinks : {};
  const technical = isRecord(data.technicalAudit) ? data.technicalAudit : {};
  const performance = isRecord(data.performance) ? data.performance : {};
  const speed = speedRows(performance.pages);
  const pagespeed = isRecord(sources?.pagespeed) ? sources.pagespeed : null;
  const failures = Array.isArray(pagespeed?.failures) ? pagespeed.failures : [];

  const sourceRows: FactSource[] = sources
    ? SOURCE_LABELS.flatMap((item) => {
        const value = sources[item.id];
        const status = statusOf(value);
        if (!status) return [];
        const detail = isRecord(value) ? text(value.detail) : null;
        return [{ id: item.id, label: item.label, status, detail }];
      })
    : [];

  if (failures.length > 0) {
    const pageSpeed = sourceRows.find((row) => row.id === "pagespeed");
    const failureText = failures
      .map((failure) => {
        if (!isRecord(failure)) return null;
        const url = text(failure.url) ?? "URL";
        const strategy = text(failure.strategy) ?? "check";
        return `${strategy} ${url}`;
      })
      .filter((line): line is string => line != null)
      .join("; ");
    if (pageSpeed && failureText) {
      pageSpeed.detail = pageSpeed.detail ? `${pageSpeed.detail}. ${failureText}` : failureText;
    }
  }

  return {
    sourcesRecorded: sourceRows.length > 0,
    sources: sourceRows,
    metrics: [
      { label: "Authority (Moz DA)", value: show(metrics.authorityScore) },
      { label: "Page authority", value: show(metrics.pageAuthority) },
      { label: "Spam score", value: show(metrics.spamScore) },
      { label: "Organic traffic", value: show(metrics.organicTrafficEstimate) },
      { label: "Organic keywords", value: show(metrics.organicKeywordCount) },
      { label: "Keywords in position 1", value: show(metrics.keywordsInPosition1) },
      { label: "Backlinks", value: show(metrics.backlinksTotal) },
      { label: "Referring domains", value: show(metrics.referringDomainsTotal) },
    ],
    keywords: keywords.slice(0, 10).map((item) => {
      const row = isRecord(item) ? item : {};
      return {
        keyword: text(row.keyword) ?? "—",
        position: show(row.position),
        volume: show(row.searchVolume),
        traffic: show(row.trafficEstimate),
      };
    }),
    keywordTotal: keywords.length,
    competitors: competitors.slice(0, 5).map((item) => {
      const row = isRecord(item) ? item : {};
      return {
        domain: text(row.domain) ?? "—",
        authority: show(row.domainAuthority),
        traffic: show(row.organicTrafficEstimate),
        keywords: show(row.organicKeywordCount),
      };
    }),
    gaps: gaps.slice(0, 10).map((item) => {
      const row = isRecord(item) ? item : {};
      const positions = Array.isArray(row.competitorPositions) ? row.competitorPositions : [];
      const first = isRecord(positions[0]) ? positions[0] : {};
      return {
        keyword: text(row.keyword) ?? "—",
        volume: show(row.searchVolume),
        competitor: text(first.domain) ?? "—",
        position: show(first.position),
      };
    }),
    gapTotal: gaps.length,
    backlinks: [
      { label: "Total backlinks", value: show(backlinks.totalBacklinks) },
      { label: "Referring domains", value: show(backlinks.referringDomains) },
      { label: "Nofollow referring domains", value: show(backlinks.referringDomainsNofollow) },
      { label: "Broken backlinks", value: show(backlinks.brokenBacklinks) },
    ],
    onPageStatus: text(technical.status),
    onPageDetail: text(technical.failureReason),
    onPageScore: show(technical.onPageScore),
    pagesCrawled: show(technical.totalPagesCrawled),
    issues: (Array.isArray(technical.errors) ? technical.errors : []).map((item) => {
      const row = isRecord(item) ? item : {};
      const samples = Array.isArray(row.sampleUrls) ? row.sampleUrls.filter((url) => typeof url === "string") : [];
      return {
        category: text(row.category) ?? "—",
        count: show(row.count),
        severity: text(row.severity) ?? "—",
        sampleUrls: samples.join(", "),
      };
    }),
    speed: speed.rows,
    legacyFid: speed.legacyFid,
    siteChecks: siteCheckRows(data.siteChecks),
  };
}

const CHECK_LABELS: Record<string, string> = {
  "soft-404-handling": "Soft 404",
  "host-variant-split": "Host variants",
  "ai-search-bot-blocked": "AI bots",
  "site-checks": "Site checks",
};

const VERDICT_LABELS: Record<string, string> = {
  pass: "Pass",
  finding: "Finding",
  unknown: "Unknown",
  choice: "Choice",
  warning: "Warning",
  not_mentioned: "Not mentioned",
};

function siteCheckRows(value: unknown): AuditFacts["siteChecks"] {
  if (!isRecord(value) || !Array.isArray(value.rows)) return [];
  return value.rows.flatMap((row) => {
    if (!isRecord(row) || typeof row.subject !== "string" || typeof row.detail !== "string") return [];
    const id = typeof row.id === "string" ? row.id : "";
    const verdict = typeof row.verdict === "string" ? row.verdict : "";
    return [{
      check: CHECK_LABELS[id] ?? id,
      subject: row.subject,
      result: VERDICT_LABELS[verdict] ?? verdict,
      detail: row.detail,
    }];
  });
}

export function figuresForPath(unsupported: UnsupportedFigure[] | undefined, path: string): string[] {
  return (unsupported ?? []).filter((item) => item.path === path).map((item) => item.figure);
}

export { highlightSegments };
